"use client";

import React from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { getImageUrl } from "@/utils/utils";
import { get } from "http";

interface AccordionData {
  description: string;
  logo: any;
  image?: any;
  statistics?: Array<{
    number: string;
    description: string;
    icon: any;
  }>;
  projectsSection?: {
    title: string;
    description: string;
    projects: Array<{
      title: string;
      image?: any;
    }>;
  };
  founderSection?: {
    title: string;
    founderName: string;
    biography: string;
    founderImage?: any;
  };
}

interface NipponKoeiAccordionProps {
  isOpen: boolean;
  data?: AccordionData;
}

const NipponKoeiAccordion: React.FC<NipponKoeiAccordionProps> = ({
  isOpen,
  data,
}) => {
  // Return early if no data is provided
  if (!data) {
    return null;
  }

  const accordionData = data;
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="overflow-hidden relative"
        >
          <div className="relative p-8 lg:p-[50px] pt-0 lg:pt-0 flex flex-col bg-white mx-auto">
            {/* Background Pattern */}
            <div
              className="absolute inset-0 opacity-30 mix-blend-luminosity pointer-events-none"
              style={{ willChange: "auto" }}
            >
              <svg
                width="1316"
                height="1468"
                viewBox="0 0 1316 1468"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full object-cover"
              >
                <g
                  style={{ mixBlendMode: "luminosity" }}
                  opacity="0.3"
                  clipPath="url(#clip0_1464_10845)"
                >
                  <path d="M3303 0H0V1468H3303V0Z" fill="#F0F0F0" />
                  <mask
                    id="mask0_1464_10845"
                    style={{ maskType: "luminance" }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="421"
                    width="968"
                    height="1047"
                  >
                    <path
                      d="M520.908 984.75L0 421.651V1468H967.947L613.743 1085.11L520.908 984.75Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_1464_10845)">
                    <path
                      d="M967.947 421.651H0V1468H967.947V421.651Z"
                      fill="url(#paint0_linear_1464_10845)"
                    />
                  </g>
                  <mask
                    id="mask1_1464_10845"
                    style={{ maskType: "luminance" }}
                    maskUnits="userSpaceOnUse"
                    x="134"
                    y="-1"
                    width="986"
                    height="1087"
                  >
                    <path
                      d="M134.496 -0.00170898C134.496 -0.00170898 410.236 729.575 520.904 984.748L613.739 1085.11L1119.72 -0.00170898H134.496Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask1_1464_10845)">
                    <path
                      d="M1119.73 -0.0012207H134.504V1085.11H1119.73V-0.0012207Z"
                      fill="url(#paint1_linear_1464_10845)"
                    />
                  </g>
                  <mask
                    id="mask2_1464_10845"
                    style={{ maskType: "luminance" }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="584"
                    height="1053"
                  >
                    <path
                      d="M0 421.651L583.963 1052.91L308.363 0H0V421.651Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask2_1464_10845)">
                    <path
                      d="M583.963 0H0V1052.91H583.963V0Z"
                      fill="url(#paint2_linear_1464_10845)"
                    />
                  </g>
                </g>
                <defs>
                  <linearGradient
                    id="paint0_linear_1464_10845"
                    x1="416.057"
                    y1="1478.9"
                    x2="526.926"
                    y2="607.062"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#E8E8E8" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_1464_10845"
                    x1="205.592"
                    y1="824.942"
                    x2="1236.17"
                    y2="134.53"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#E8E8E8" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_1464_10845"
                    x1="181.154"
                    y1="1049.06"
                    x2="362.072"
                    y2="195.952"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#E8E8E8" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <clipPath id="clip0_1464_10845">
                    <rect width="3303" height="1468" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>

            <div className="relative z-10">
              <div className="mx-auto">
                {/* Content Container */}
                <div className="flex flex-col gap-8">
                  {/* Product Description */}
                  <div className="text-gray-800 text-justify font-inter text-base leading-7">
                    {accordionData?.description}
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-7 ">
                    {accordionData?.statistics.map((stat, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-2.5 bg-[#0079BD] rounded-[20px] max-w-[220px] min-h-[220px] p-5 pt-16 pb-8 relative"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="text-white font-inter text-[40px] font-bold leading-12 tracking-[-1.44px] mb-2.5">
                            {stat.number}
                          </div>
                          <div className="text-white font-inter text-base leading-6 text-center">
                            {stat.description}
                          </div>
                        </div>
                        <div className="absolute top-0 left-5 flex p-[10px] w-11 h-11 items-center rounded-bl-lg rounded-br-lg bg-[#F4F5F6]">
                          {stat?.icon && (
                            <Image
                              src={getImageUrl(stat?.icon?.url || "")}
                              alt={stat.description}
                              width={24}
                              height={24}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Projects Section */}
                  <div className="flex flex-col gap-8">
                    <div>
                      {/* Project Header */}
                      <div className="pb-2 border-b border-[#F4F5F6]">
                        <h3 className="text-gray-900 font-inter text-xl font-medium leading-7 tracking-[-0.72px] text-left">
                          {accordionData?.projectsSection?.title}
                        </h3>
                      </div>

                      {/* Project Description */}
                      <div className="text-gray-800 font-inter text-base leading-7 text-justify pt-3">
                        {accordionData?.projectsSection?.description}
                      </div>
                    </div>

                    {/* Project Details */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
                      {accordionData?.projectsSection?.projects.map(
                        (project, index) => (
                          <div key={index} className="flex flex-col">
                            <div className="relative w-full h-80 rounded-2xl overflow-hidden bg-gray-200 mb-5">
                              {project.image && (
                                <Image
                                  src={getImageUrl(project.image?.url || "")}
                                  alt={project.title}
                                  fill
                                  className="object-cover"
                                />
                              )}
                            </div>
                            <div className="text-center px-3 py-5">
                              <h4 className="text-gray-800 font-inter text-lg font-medium leading-[22px] tracking-[-0.72px]">
                                {project.title}
                              </h4>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Founder Section */}
                  <div className="flex flex-col gap-3">
                    {/* Founder Header */}
                    <div className="pb-2 border-b border-[#F4F5F6]">
                      <h3 className="text-gray-900 font-inter text-xl font-medium leading-7 tracking-[-0.72px] text-left">
                        {accordionData?.founderSection?.title}
                      </h3>
                    </div>

                    {/* Founder Details */}
                    <div className="flex flex-col lg:flex-row gap-7 items-start">
                      {/* Founder Image */}
                      <div className="flex flex-col pt-1.5">
                        <div className="relative w-[245px] h-40 rounded-2xl overflow-hidden bg-gray-200 mb-5">
                          {accordionData?.founderSection?.founderImage && (
                            <Image
                              src={getImageUrl(
                                accordionData?.founderSection?.founderImage
                                  ?.url || ""
                              )}
                              alt={accordionData?.founderSection?.founderName}
                              fill
                              className="object-cover"
                            />
                          )}
                        </div>
                        <div className="text-center px-3 py-5">
                          <h4 className="text-gray-800 font-inter text-lg font-medium leading-4 tracking-[-0.72px]">
                            {accordionData?.founderSection?.founderName}
                          </h4>
                        </div>
                      </div>

                      {/* Founder Description */}
                      <div className="flex-1 text-gray-800 font-inter text-base leading-7 text-justify">
                        {accordionData?.founderSection?.biography}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {/* <div className="flex h-0.5 bg-gray-25 relative">
            <div className="w-full h-0.5 bg-[#1C8579] absolute left-0 top-0"></div>
          </div> */}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NipponKoeiAccordion;
